import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

const PRESET_COLORS = [
  "Black",
  "White",
  "Blue",
  "Red",
  "Green",
  "Purple",
  "Silver",
  "Gold",
  "Rose Gold",
  "Navy",
  "Orange",
  "Yellow",
];

export default function Products() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [filters, setFilters] = useState({ category: "", model: "" });
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({
    name: "",
    category: "",
    model: "",
    colors: [],
  });
  const [newColor, setNewColor] = useState({
    color: "",
    stock: 0,
    buyPrice: 0,
    sellPrice: 0,
  });
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchData = async () => {
    const params = new URLSearchParams(filters).toString();
    const res = await axios.get(
      `https://vape-backend-1.onrender.com/api/products?${params}`,
    );
    setProducts(res.data);
    const meta = await axios.get(
      "https://vape-backend-1.onrender.com/api/products/meta/categories",
    );
    setCategories(meta.data.categories);
    setModels(meta.data.models);
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const openAdd = () => {
    setEditProduct(null);
    setForm({ name: "", category: "", model: "", colors: [] });
    setNewColor({ color: "", stock: 0, buyPrice: 0, sellPrice: 0 });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p.name,
      category: p.category,
      model: p.model,
      colors: p.colors.map((c) => ({ ...c })),
    });
    setNewColor({ color: "", stock: 0, buyPrice: 0, sellPrice: 0 });
    setShowModal(true);
  };

  const addColor = () => {
    const colorName = newColor.color.trim();
    if (!colorName) return toast.error("Color name required");
    // Duplicate check on frontend
    const existing = form.colors.find(
      (c) => c.color.toLowerCase() === colorName.toLowerCase(),
    );
    if (existing) return toast.error(`"${colorName}" color already added!`);
    setForm({
      ...form,
      colors: [...form.colors, { ...newColor, color: colorName }],
    });
    setNewColor({ color: "", stock: 0, buyPrice: 0, sellPrice: 0 });
  };

  const removeColor = (i) => {
    setForm({ ...form, colors: form.colors.filter((_, idx) => idx !== i) });
  };

  const updateColorStock = (i, field, value) => {
    const updated = [...form.colors];
    updated[i] = { ...updated[i], [field]: field === "color" ? value : +value };
    setForm({ ...form, colors: updated });
  };

  const submit = async () => {
    if (!form.name || !form.category || !form.model)
      return toast.error("Fill all fields");
    if (form.colors.length === 0) return toast.error("Add at least one color");
    try {
      let res;
      if (editProduct) {
        res = await axios.put(
          `https://vape-backend-1.onrender.com/api/products/${editProduct._id}`,
          form,
        );
      } else {
        res = await axios.post(
          "https://vape-backend-1.onrender.com/api/products",
          form,
        );
      }
      if (res.data.merged) {
        toast.success("Colors merged into existing product!");
      } else {
        toast.success(isAdmin ? "Saved!" : "Submitted for approval!");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(
        `https://vape-backend-1.onrender.com/api/products/${id}`,
      );
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Error");
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">Manage your vape product inventory</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          + Add Product
        </button>
      </div>

      <div className="filter-bar">
        <select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filters.model}
          onChange={(e) => setFilters({ ...filters, model: e.target.value })}
        >
          <option value="">All Models</option>
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop table */}
      <div className="card hide-mobile">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Model</th>
                <th>Colors & Stock</th>
                <th>Total Stock</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="icon">📦</div>
                      <p>No products found</p>
                    </div>
                  </td>
                </tr>
              )}
              {products.map((p) => (
                <tr key={p._id}>
                  <td style={{ fontWeight: 600 }}>{p.name}</td>
                  <td>
                    <span
                      className="badge-color"
                      style={{
                        background: "rgba(0,229,255,0.1)",
                        color: "var(--accent)",
                      }}
                    >
                      {p.category}
                    </span>
                  </td>
                  <td style={{ color: "var(--text-secondary)" }}>{p.model}</td>
                  <td>
                    {p.colors.map((c) => (
                      <span key={c.color} className="color-chip">
                        <span
                          className="color-dot"
                          style={{ background: colorHex(c.color) }}
                        ></span>
                        {c.color}:{" "}
                        <strong
                          style={{
                            color:
                              c.stock === 0
                                ? "var(--danger)"
                                : "var(--text-primary)",
                          }}
                        >
                          {c.stock}
                        </strong>
                        <span
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "0.72rem",
                          }}
                        >
                          {" "}
                          (৳{c.sellPrice})
                        </span>
                      </span>
                    ))}
                  </td>
                  <td
                    style={{
                      fontWeight: 700,
                      color:
                        p.totalStock === 0 ? "var(--danger)" : "var(--success)",
                    }}
                  >
                    {p.totalStock}
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => openEdit(p)}
                    >
                      Edit
                    </button>
                    {isAdmin && (
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: 6 }}
                        onClick={() => deleteProduct(p._id)}
                      >
                        Del
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="show-mobile">
        {products.length === 0 && (
          <div className="card">
            <div className="empty-state">
              <div className="icon">📦</div>
              <p>No products found</p>
            </div>
          </div>
        )}
        {products.map((p) => (
          <div className="card mobile-product-card" key={p._id}>
            <div
              className="mobile-card-header"
              onClick={() =>
                setExpandedRow(expandedRow === p._id ? null : p._id)
              }
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "1rem" }}>
                  {p.name}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    className="badge-color"
                    style={{
                      background: "rgba(0,229,255,0.1)",
                      color: "var(--accent)",
                      fontSize: "0.72rem",
                    }}
                  >
                    {p.category}
                  </span>
                  <span
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "0.8rem",
                    }}
                  >
                    {p.model}
                  </span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color:
                      p.totalStock === 0 ? "var(--danger)" : "var(--success)",
                  }}
                >
                  {p.totalStock}
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                  Total Stock
                </div>
                <div
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "0.8rem",
                    marginTop: 4,
                  }}
                >
                  {expandedRow === p._id ? "▲" : "▼"}
                </div>
              </div>
            </div>
            {expandedRow === p._id && (
              <div className="mobile-card-body">
                <div style={{ marginBottom: 10 }}>
                  {p.colors.map((c) => (
                    <div key={c.color} className="mobile-color-row">
                      <span
                        className="color-dot"
                        style={{ background: colorHex(c.color) }}
                      ></span>
                      <span style={{ fontWeight: 500, minWidth: 60, flex: 1 }}>
                        {c.color}
                      </span>
                      <span
                        style={{
                          color:
                            c.stock === 0 ? "var(--danger)" : "var(--success)",
                          fontWeight: 700,
                        }}
                      >
                        Qty: {c.stock}
                      </span>
                      <span
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.78rem",
                          marginLeft: "auto",
                        }}
                      >
                        ৳{c.sellPrice}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flex: 1 }}
                    onClick={() => openEdit(p)}
                  >
                    Edit
                  </button>
                  {isAdmin && (
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => deleteProduct(p._id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <h3 style={{ margin: 0 }}>
                {editProduct ? "Edit Product" : "Add New Product"}
              </h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowModal(false)}
                style={{ padding: "4px 10px" }}
              >
                ✕
              </button>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Product Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Vozol Neon"
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  placeholder="e.g. Culnivar"
                  list="cats"
                />
                <datalist id="cats">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="form-group">
              <label>Model</label>
              <input
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="e.g. G3 Lite SE"
                list="mods"
              />
              <datalist id="mods">
                {models.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </div>

            {/* Color list */}
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.78rem",
                  color: "var(--text-secondary)",
                  marginBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Colors & Stock ({form.colors.length} added)
              </label>
              {form.colors.map((c, i) => (
                <div key={i} className="color-edit-row">
                  <span
                    className="color-dot"
                    style={{ background: colorHex(c.color), flexShrink: 0 }}
                  ></span>
                  <span
                    style={{
                      fontWeight: 600,
                      minWidth: 60,
                      fontSize: "0.85rem",
                    }}
                  >
                    {c.color}
                  </span>
                  <div className="color-edit-inputs">
                    <div className="color-edit-field">
                      <span className="color-edit-label">Qty</span>
                      <input
                        type="number"
                        className="color-mini-input"
                        value={c.stock}
                        min="0"
                        onChange={(e) =>
                          updateColorStock(i, "stock", e.target.value)
                        }
                      />
                    </div>
                    <div className="color-edit-field">
                      <span className="color-edit-label">Buy ৳</span>
                      <input
                        type="number"
                        className="color-mini-input"
                        value={c.buyPrice}
                        min="0"
                        onChange={(e) =>
                          updateColorStock(i, "buyPrice", e.target.value)
                        }
                      />
                    </div>
                    <div className="color-edit-field">
                      <span className="color-edit-label">Sell ৳</span>
                      <input
                        type="number"
                        className="color-mini-input"
                        value={c.sellPrice}
                        min="0"
                        onChange={(e) =>
                          updateColorStock(i, "sellPrice", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ padding: "4px 8px", flexShrink: 0 }}
                    onClick={() => removeColor(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {/* Add new color section */}
              <div className="add-color-box">
                <div className="grid-2">
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Color Name</label>
                    <input
                      value={newColor.color}
                      onChange={(e) =>
                        setNewColor({ ...newColor, color: e.target.value })
                      }
                      placeholder="e.g. Black"
                      list="clrs"
                      onKeyDown={(e) => e.key === "Enter" && addColor()}
                    />
                    <datalist id="clrs">
                      {PRESET_COLORS.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Stock Qty</label>
                    <input
                      type="number"
                      value={newColor.stock}
                      onChange={(e) =>
                        setNewColor({ ...newColor, stock: +e.target.value })
                      }
                      min="0"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Buy Price ৳</label>
                    <input
                      type="number"
                      value={newColor.buyPrice}
                      onChange={(e) =>
                        setNewColor({ ...newColor, buyPrice: +e.target.value })
                      }
                      min="0"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Sell Price ৳</label>
                    <input
                      type="number"
                      value={newColor.sellPrice}
                      onChange={(e) =>
                        setNewColor({ ...newColor, sellPrice: +e.target.value })
                      }
                      min="0"
                    />
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={addColor}
                  style={{ width: "100%" }}
                >
                  + Add Color
                </button>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submit}>
                {isAdmin ? "Save" : "Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function colorHex(name) {
  const map = {
    black: "#222",
    white: "#eee",
    blue: "#4a90d9",
    red: "#e74c3c",
    green: "#2ecc71",
    purple: "#9b59b6",
    silver: "#aaa",
    gold: "#f1c40f",
    navy: "#2c3e50",
    orange: "#e67e22",
    yellow: "#f9ca24",
    "rose gold": "#b5838d",
  };
  return map[name?.toLowerCase()] || "#555";
}
